import React, { useState } from 'react';
import { 
  Calculator, 
  TrendingUp, 
  BarChart3, 
  Upload, 
  Download,
  Play,
  Settings,
  FileText,
  Activity,
  Zap
} from 'lucide-react';

interface AnalysisResult {
  rSquared: number;
  pValue: number;
  coefficients: Array<{
    variable: string;
    coefficient: number;
    standardError: number;
    tStat: number;
    pValue: number;
  }>;
  residuals: number[];
  predictions: number[];
}

const Econometrics: React.FC = () => {
  const [selectedAnalysis, setSelectedAnalysis] = useState<'regression' | 'timeseries' | 'panel'>('regression');
  const [dataUploaded, setDataUploaded] = useState(false);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResult | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const analysisTypes = [
    {
      id: 'regression',
      name: 'Linear Regression',
      description: 'Analyze relationships between variables using OLS regression',
      icon: TrendingUp,
      color: 'blue'
    },
    {
      id: 'timeseries',
      name: 'Time Series Analysis',
      description: 'Forecast economic indicators using ARIMA and other models',
      icon: Activity,
      color: 'green'
    },
    {
      id: 'panel',
      name: 'Panel Data Analysis',
      description: 'Analyze cross-sectional and time series data combined',
      icon: BarChart3,
      color: 'purple'
    }
  ];

  const mockResult: AnalysisResult = {
    rSquared: 0.847,
    pValue: 0.001,
    coefficients: [
      {
        variable: 'Intercept',
        coefficient: 2.456,
        standardError: 0.123,
        tStat: 19.97,
        pValue: 0.000
      },
      {
        variable: 'GDP_Growth',
        coefficient: 0.789,
        standardError: 0.045,
        tStat: 17.53,
        pValue: 0.000
      },
      {
        variable: 'Inflation_Rate',
        coefficient: -0.234,
        standardError: 0.067,
        tStat: -3.49,
        pValue: 0.001
      },
      {
        variable: 'Unemployment',
        coefficient: -0.156,
        standardError: 0.089,
        tStat: -1.75,
        pValue: 0.083
      }
    ],
    residuals: [0.12, -0.08, 0.15, -0.11, 0.07, -0.13, 0.09, -0.05],
    predictions: [3.2, 3.8, 2.9, 4.1, 3.5, 2.7, 3.9, 3.3]
  };

  const runAnalysis = () => {
    setIsRunning(true);
    // Simulate analysis
    setTimeout(() => {
      setAnalysisResult(mockResult);
      setIsRunning(false);
    }, 2000);
  };

  // const getAnalysisColor = (type: string) => {
  //   const analysis = analysisTypes.find(a => a.id === type);
  //   return analysis?.color || 'blue';
  // };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Econometrics Lab</h1>
        <p className="mt-2 text-gray-600">
          Advanced statistical analysis tools for economic research and forecasting
        </p>
      </div>

      {/* Analysis Type Selection */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Choose Analysis Type</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {analysisTypes.map(analysis => {
            const Icon = analysis.icon;
            const isSelected = selectedAnalysis === analysis.id;
            return (
              <button
                key={analysis.id}
                onClick={() => setSelectedAnalysis(analysis.id as any)}
                className={`p-6 rounded-lg border-2 transition-all ${
                  isSelected
                    ? `border-${analysis.color}-500 bg-${analysis.color}-50`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Icon className={`h-8 w-8 mx-auto mb-3 ${
                  isSelected ? `text-${analysis.color}-600` : 'text-gray-400'
                }`} />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{analysis.name}</h3>
                <p className="text-sm text-gray-600">{analysis.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Data Upload and Configuration */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Upload */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Input</h3>
          
          {!dataUploaded ? (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 mb-2">Upload your dataset</p>
              <p className="text-sm text-gray-500 mb-4">CSV, Excel, or JSON files supported</p>
              <button 
                onClick={() => setDataUploaded(true)}
                className="btn-primary"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Data
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center p-3 bg-green-50 rounded-lg">
                <FileText className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-sm text-green-800">economic_data.csv uploaded successfully</span>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Dataset Preview</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2">Year</th>
                        <th className="text-left py-2">GDP_Growth</th>
                        <th className="text-left py-2">Inflation</th>
                        <th className="text-left py-2">Unemployment</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2">2020</td>
                        <td className="py-2">-3.1</td>
                        <td className="py-2">1.2</td>
                        <td className="py-2">8.1</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2">2021</td>
                        <td className="py-2">5.7</td>
                        <td className="py-2">4.7</td>
                        <td className="py-2">5.4</td>
                      </tr>
                      <tr>
                        <td className="py-2">2022</td>
                        <td className="py-2">2.1</td>
                        <td className="py-2">8.0</td>
                        <td className="py-2">3.7</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Model Configuration */}
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Model Configuration</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dependent Variable
              </label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option>GDP_Growth</option>
                <option>Inflation</option>
                <option>Unemployment</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Independent Variables
              </label>
              <div className="space-y-2">
                {['Inflation', 'Unemployment', 'Interest_Rate', 'Government_Spending'].map(variable => (
                  <label key={variable} className="flex items-center">
                    <input type="checkbox" className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" defaultChecked />
                    <span className="ml-2 text-sm text-gray-700">{variable}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confidence Level
              </label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500">
                <option>95%</option>
                <option>90%</option>
                <option>99%</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Run Analysis */}
      <div className="card text-center">
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={runAnalysis}
            disabled={!dataUploaded || isRunning}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRunning ? (
              <>
                <Zap className="h-4 w-4 mr-2 animate-pulse" />
                Running Analysis...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Run {analysisTypes.find(a => a.id === selectedAnalysis)?.name}
              </>
            )}
          </button>
          
          <button className="btn-secondary">
            <Settings className="h-4 w-4 mr-2" />
            Advanced Options
          </button>
        </div>
      </div>

      {/* Results */}
      {analysisResult && (
        <div className="space-y-6">
          {/* Summary Statistics */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Regression Results</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{analysisResult.rSquared.toFixed(3)}</div>
                <div className="text-sm text-gray-600">R-squared</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{analysisResult.pValue.toFixed(3)}</div>
                <div className="text-sm text-gray-600">F-statistic p-value</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{analysisResult.coefficients.length}</div>
                <div className="text-sm text-gray-600">Variables</div>
              </div>
            </div>

            {/* Coefficients Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full border border-gray-200 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Variable
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coefficient
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Std. Error
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      t-Statistic
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      p-value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {analysisResult.coefficients.map((coef, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {coef.variable}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {coef.coefficient.toFixed(3)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {coef.standardError.toFixed(3)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                        {coef.tStat.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          coef.pValue < 0.05 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {coef.pValue.toFixed(3)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Export Options */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Export Results</h3>
            <div className="flex flex-wrap gap-3">
              <button className="btn-secondary">
                <Download className="h-4 w-4 mr-2" />
                Download Report (PDF)
              </button>
              <button className="btn-secondary">
                <Download className="h-4 w-4 mr-2" />
                Export Data (CSV)
              </button>
              <button className="btn-secondary">
                <Calculator className="h-4 w-4 mr-2" />
                Save Model
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Econometrics; 